import { LanguageContext } from '@axon-enterprise/i18n-helpers';
import React, { forwardRef, useContext, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

/** Maps app locale (e.g. en-us) to Google reCAPTCHA `hl` codes. */
function mapAppLanguageToRecaptchaHl(language: string): string {
  const normalized = language.toLowerCase();
  const explicit: Record<string, string> = {
    'en-us': 'en',
    es: 'es',
    'fr-ca': 'fr-CA',
    sk: 'sk',
  };
  if (explicit[normalized]) {
    return explicit[normalized];
  }
  const [primary] = normalized.split('-');
  return primary || 'en';
}

interface ReCaptchaProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: Error) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark';
  size?: 'compact' | 'normal' | 'invisible';
  className?: string;
  disabled?: boolean;
}

export interface ReCaptchaRef {
  reset: () => void;
  execute: () => void;
  getValue: () => string | null;
}

export const ReCaptcha = forwardRef<ReCaptchaRef, ReCaptchaProps>(
  ({ siteKey, onVerify, onError, onExpire, theme = 'light', size = 'normal', className, disabled }, ref) => {
    const { language } = useContext(LanguageContext);
    const recaptchaHl = useMemo(() => mapAppLanguageToRecaptchaHl(language), [language]);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useImperativeHandle(ref, () => ({
      reset: () => {
        recaptchaRef.current?.reset();
        setError(null);
      },
      execute: () => {
        if (size === 'invisible') {
          recaptchaRef.current?.execute();
        }
      },
      getValue: () => {
        return recaptchaRef.current?.getValue() || null;
      }
    }));

    const handleVerify = (token: string | null) => {
      setIsLoading(false);
      setError(null);

      if (token) {
        onVerify(token);
      } else {
        const message = __('reCAPTCHA verification failed. Please try again.');
        setError(message);
        onError?.(new Error(message));
      }
    };

    const handleError = () => {
      setIsLoading(false);
      const message = __('reCAPTCHA failed to load');
      setError(message);
      onError?.(new Error(message));
    };

    const handleExpire = () => {
      setError(__('reCAPTCHA expired'));
      onExpire?.();
    };

    const handleLoad = () => {
      setIsLoading(false);
      setError(null);
    };

    if (!siteKey) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                {__('reCAPTCHA configuration error: Site key is required')}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`recaptcha-container ${className || ''}`}>
        <ReCAPTCHA
          key={recaptchaHl}
          ref={recaptchaRef}
          sitekey={siteKey}
          onChange={handleVerify}
          onErrored={handleError}
          onExpired={handleExpire}
          onLoad={handleLoad}
          theme={theme}
          size={size}
          hl={recaptchaHl}
        />
        
        {isLoading && (
          <div className="mt-2 text-sm text-gray-600">
            {__('Loading reCAPTCHA...')}
          </div>
        )}
        
        {error && (
          <div className="mt-2 text-sm text-red-600">
            {error}
          </div>
        )}
        
        {disabled && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-50 rounded cursor-not-allowed" />
        )}
      </div>
    );
  }
);

ReCaptcha.displayName = 'ReCaptcha';

export default ReCaptcha; 